<template>
  <div class="app-notification-container container">
    <TransitionGroup name="app-notification-container__transition">
      <AppNotification
        v-for="notification in topNotifications"
        :key="notification.id"
        :variant="notification.variant"
        :actions="notification.actions"
        :confetti="notification.confetti"
        @close="appNotifications.dismiss(notification.id)"
      >
        <template v-if="notification.title" v-slot:title>{{
          notification.title
        }}</template>
        <template v-slot:message>{{ notification.message }}</template>
      </AppNotification>
    </TransitionGroup>
  </div>

  <div
    class="app-notification-container app-notification-container--bottom container"
  >
    <TransitionGroup name="app-notification-container__transition">
      <AppNotification
        v-for="notification in compactNotifications"
        :key="notification.id"
        :variant="notification.variant"
        :actions="notification.actions"
        :compact="true"
      >
        <template v-slot:message>{{ notification.message }}</template>
      </AppNotification>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { appNotifications } from '@/services/notification.app'
import AppNotification from '@/app/components/AppNotification.vue'
import { computed } from 'vue'

const topNotifications = computed(() =>
  appNotifications.notifications.filter((n) => !n.compact),
)
const compactNotifications = computed(() =>
  appNotifications.notifications.filter((n) => n.compact),
)
</script>

<style lang="scss" scoped>
.app-notification-container {
  position: fixed;
  left: 0;
  right: 0;
  top: 92px;
  // Action confirmations must remain visible above the completion popup (11000).
  z-index: 11001;
  display: grid;
  gap: 10px;

  @media only screen and (max-width: $w-tablet-s) {
    top: 65px;
  }

  &--bottom {
    // Sit above the mobile navbar when it's shown, or the iOS home
    // indicator when it isn't.
    --bottom-inset: max(
      var(--navbar-mobile-height, 0px),
      env(safe-area-inset-bottom, 0px)
    );

    top: auto;
    bottom: calc(var(--bottom-inset) + 24px);
    max-width: 630px;

    @media (max-width: $w-tablet-s) {
      bottom: calc(var(--bottom-inset) + 16px);
    }
  }

  &--bottom &__transition-enter-from {
    transform: translateY(100px);
  }

  &__transition {
    &-enter {
      &-active {
        transition:
          opacity 0.3s linear(0, 0.7 15%, 1.05 33%, 1.1 50%, 1),
          transform 0.3s linear(0, 0.7 15%, 1.05 33%, 1.1 50%, 1);
      }

      &-from {
        transform: translateY(-100px);
        opacity: 0.5;
      }

      &-to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    &-leave {
      &-active {
        width: calc(100% - var(--container-padding) * 2);
        margin-inline: var(--container-padding);
        position: absolute;
      }

      &-from {
        opacity: 1;
      }

      &-to {
        opacity: 0;
      }
    }

    &-leave-active,
    &-move {
      transition:
        opacity 0.3s,
        transform 0.3s;
      z-index: -1;
    }
  }
}
</style>
